/**
 * shell/useComposerQueue.ts —— **本地发送队列**（★20261011 大王决策「插话/排队照抄 dsh」）
 *
 * dsh 依据（224 实跑件，逐字）：`dsh-client-ui-conversation/README.zh.md:81-83`
 *   「`sendSession` 在序列化之前用投递模式注册 Session 提交回显（`session.beginSubmission`）…」
 *   「排队提交的本地回显在禁用的编辑、删除、插话按钮旁显示 `发送中…`；折叠…」
 *   中文串：`queue.sending`＝「发送中…」；失败文案 `queue.editFailed`／`queue.removeFailed`／`queue.steerFailed`。
 *   ⇒ dsh 的「排队」是**客户端本地队列**：入队不出网，运行结束后依次投递；坞内可 编辑／删除／**插话**（提前投）。
 *
 * 🔴 落点差异（本仓纪律，**有意**，非简写）：dsh 的回显在**消息流内**（用户气泡旁 `data-submission-echo`）。
 *   本仓铁律「结构与组件树**只由声明/投影生成**、前端零取数」（`shell/AppShell.tsx` 头注）
 *   ⇒ 本地队列条目**不是投影帧**，塞进中栏流会破该纪律；故回显落在**输入区上方**（`composer-queue-dock`），
 *   功能（入队／编辑／删除／插话／发送中…）与 dsh 对齐，**位置按本仓纪律**。
 *
 * 状态机（每项）：`queued`（在队）→ `sending`（正在投递）→ 出队（成功）／`failed`（投递被拒，留队可重试）。
 */

import { useCallback, useEffect, useRef, useState } from "react";

export type QueueItemStatus = "queued" | "sending" | "failed";

export interface QueueItem {
  id: string;
  text: string;
  status: QueueItemStatus;
  /** 失败原因（`status==="failed"` 时有值；dsh `queue.editFailed` 同义面） */
  error?: string;
}

export interface UseComposerQueueOptions {
  /** 运行中＝true（宿主 `props.running`）；由 true→false 触发**依次投递** */
  running: boolean;
  /** 真正投递（返回 false ＝被拒／未连 ⇒ 该项留队并标 failed） */
  deliver: (text: string, item: QueueItem) => boolean;
  /** 编辑项正文（dsh `queue.edit`）；缺省 ⇒ 只改本地文本 */
  onEdit?: (id: string, text: string) => void;
  /** 删除项（dsh `queue.remove`） */
  onRemove?: (id: string) => void;
  /** 插话：把该项提前投递（dsh `queue.steer`）；返回 false ⇒ 标 failed */
  onSteer?: (text: string, item: QueueItem) => boolean;
}

export interface ComposerQueue {
  items: QueueItem[];
  /** 入队（运行中且模式=queue 时的提交去向）；返回项 id */
  enqueue: (text: string) => string;
  edit: (id: string, text: string) => void;
  remove: (id: string) => void;
  /** 插话（提前投递）；成功 ⇒ 出队 */
  steer: (id: string) => void;
  /** 清空（会话切换/登出等宿主显式动作） */
  clear: () => void;
  /** 取某项文本（编辑用；不存在 ⇒ null）——外置消费面（中栏气泡三键）用 */
  take: (id: string) => string | null;
}

let seq = 0;
const nextId = (): string => "q-local-" + (++seq);

export function useComposerQueue(opts: UseComposerQueueOptions): ComposerQueue {
  const { running, deliver, onEdit, onRemove, onSteer } = opts;
  const [items, setItems] = useState<QueueItem[]>([]);
  const itemsRef = useRef<QueueItem[]>([]);
  useEffect(() => { itemsRef.current = items; }, [items]);
  const prevRunning = useRef(running);
  const draining = useRef(false);

  const enqueue = useCallback((text: string): string => {
    const id = nextId();
    setItems((cur) => [...cur, { id, text, status: "queued" }]);
    return id;
  }, []);

  const edit = useCallback((id: string, text: string): void => {
    setItems((cur) => cur.map((it) => (it.id === id ? { ...it, text } : it)));
    onEdit?.(id, text);
  }, [onEdit]);

  const remove = useCallback((id: string): void => {
    setItems((cur) => cur.filter((it) => it.id !== id));
    onRemove?.(id);
  }, [onRemove]);

  const steer = useCallback((id: string): void => {
    const it = itemsRef.current.find((x) => x.id === id);
    if (!it) return;
    const ok = onSteer ? onSteer(it.text, it) : deliver(it.text, it);
    if (ok) setItems((cur) => cur.filter((x) => x.id !== id));
    else setItems((cur) => cur.map((x) => (x.id === id ? { ...x, status: "failed", error: "插话失败" } : x)));
  }, [deliver, onSteer]);

  const clear = useCallback((): void => setItems([]), []);

  const take = useCallback((id: string): string | null => {
    const it = itemsRef.current.find((x) => x.id === id);
    return it ? it.text : null;
  }, []);

  /** 运行结束（true→false）⇒ **依次投递**（串行；投递失败 ⇒ 标 failed 留队，不阻断后续）
   *  🔴 实现要点：本轮待投项取**快照**（`itemsRef.current` 要等重渲染才更新，直接循环读它会**死循环**）。 */
  useEffect(() => {
    const wasRunning = prevRunning.current;
    prevRunning.current = running;
    if (!(wasRunning && !running)) return;
    if (draining.current) return;
    const drain = async (): Promise<void> => {
      draining.current = true;
      try {
        const pending = itemsRef.current.filter((x) => x.status === "queued" || x.status === "failed");
        for (const it of pending) {
          setItems((cur) => cur.map((x) => (x.id === it.id ? { ...x, status: "sending" } : x)));
          const ok = deliver(it.text, { ...it, status: "sending" });
          if (ok) setItems((cur) => cur.filter((x) => x.id !== it.id));
          else {
            setItems((cur) => cur.map((x) => (x.id === it.id ? { ...x, status: "failed", error: "投递被拒" } : x)));
            break;                                       // 被拒 ⇒ 停止本轮，避免连锁假发
          }
        }
      } finally {
        draining.current = false;
      }
    };
    void drain();
  }, [running, deliver]);

  return { items, enqueue, edit, remove, steer, clear, take };
}

export default useComposerQueue;
